// services/bankingApi.ts
// Multi-banking API: bank account CRUD, tier-account pooling, and the
// branch-manager login for the branch portal. All under /lords/banking and
// /lords/branches (staff-authenticated, gated by canManageBanking/Branches).
import { apiFetch } from '../utils/api';
import type {
  BankAccount,
  BankAccountInput,
  TierAccounts,
  BankStatus,
  BranchManager,
} from '../types/banking';

const BASE = 'https://api.playbro.com';

// Multipart helper for create/update (carries the optional qr_image file).
// apiFetch forces JSON, so this mirrors its auth handling for FormData bodies.
async function mpFetch<T = any>(path: string, method: string, form: FormData): Promise<T> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json();
}

function toForm(payload: Partial<BankAccountInput>, qr?: File | null): FormData {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') form.append(k, String(v));
  });
  if (qr) form.append('qr_image', qr);
  return form;
}

// ─── Accounts ───────────────────────────────────────────────────

export const listAccounts = (params: { method?: string; mode?: string; status?: string } = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
  const s = qs.toString();
  return apiFetch<{ data: BankAccount[] }>(`/lords/banking/accounts${s ? `?${s}` : ''}`)
    .then((r: any) => (r?.data ?? []) as BankAccount[]);
};

export const createAccount = (payload: BankAccountInput, qr?: File | null) =>
  mpFetch<BankAccount>('/lords/banking/accounts', 'POST', toForm(payload, qr));

export const updateAccount = (id: number, payload: Partial<BankAccountInput>, qr?: File | null) =>
  mpFetch<BankAccount>(`/lords/banking/accounts/${id}`, 'PUT', toForm(payload, qr));

export const updateAccountStatus = (id: number, status: BankStatus) =>
  apiFetch<BankAccount>(`/lords/banking/accounts/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

export const deleteAccount = (id: number) =>
  apiFetch(`/lords/banking/accounts/${id}`, { method: 'DELETE' });

// ─── Tier ↔ account pooling ─────────────────────────────────────

export const getTierAccounts = (tierId: number) =>
  apiFetch<TierAccounts>(`/lords/banking/tiers/${tierId}/accounts`);

export const assignTierAccount = (tierId: number, bankAccountId: number) =>
  apiFetch(`/lords/banking/tiers/${tierId}/accounts`, {
    method: 'POST',
    body: JSON.stringify({ bankAccountId }),
  });

export const unassignTierAccount = (tierId: number, accountId: number) =>
  apiFetch(`/lords/banking/tiers/${tierId}/accounts/${accountId}`, { method: 'DELETE' });

// ─── Branch manager (branch-portal login) ───────────────────────

export const getBranchManager = (branchId: number) =>
  apiFetch<{ manager: BranchManager | null }>(`/lords/branches/${branchId}/manager`);

export const setBranchManager = (
  branchId: number,
  payload: { username: string; email?: string; password?: string }
) =>
  apiFetch(`/lords/branches/${branchId}/manager`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
